import {useCallback, useEffect} from 'react';
import {app, authentication, HostClientType} from '@microsoft/teams-js';
import {getIsMsTeamsEnv} from '@lib/infrastructure';
import {
  disconnectCalendar,
  enableCalendarAfterRedirection,
  getConsentUrlForCalendar,
  setIsSigningOut,
  showTimedFeedback,
  signInPopUp,
  signOut,
  signOutPopup,
  silentSignIn,
  useAppDispatch,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {useMsal} from '@azure/msal-react';

export const useIsTeamsApp = () => {
  return getIsMsTeamsEnv();
};

export const useMsTeams = () => {
  const isTeamsApp = useIsTeamsApp();

  useEffect(() => {
    const initTeams = async () => {
      try {
        await app.initialize();
        console.debug('Teams SDK initialized');
        app.notifySuccess();
      } catch (error) {
        console.debug('Teams SDK initialization failed:', error);
      }
    };

    if (isTeamsApp) {
      initTeams();
    }
  }, [isTeamsApp]);
};

export const useTeamSignIn = () => {
  const dispatch = useAppDispatch();
  const {instance} = useMsal();
  const isTeamsApp = useIsTeamsApp();

  const login = useCallback(async () => {
    if (!isTeamsApp) return;

    try {
      const clientType = (await app.getContext()).app.host.clientType;
      if (clientType === HostClientType.web) {
        dispatch(withAsyncThunkErrorHandling(() => signInPopUp()));
        return;
      }

      // desktop app
      const result = await authentication.authenticate({
        url: window.location.origin + '/teams-signin',
        width: 600,
        height: 535,
      });

      instance.setActiveAccount(JSON.parse(result));
      // this will setup all the store tokens and flags for user logged in
      dispatch(withAsyncThunkErrorHandling(() => silentSignIn()));
    } catch (err) {
      const error = err as Error;
      console.debug('Auth failed:', error);

      if ((error as Error).message !== 'CancelledByUser') {
        dispatch(showTimedFeedback({message: error.message, type: 'error'}));
      }
    }
  }, [dispatch, instance, isTeamsApp]);

  return login;
};

export const useTeamsSignOut = () => {
  const dispatch = useAppDispatch();
  const isTeamsApp = useIsTeamsApp();

  const logout = useCallback(async () => {
    if (!isTeamsApp) return;

    dispatch(setIsSigningOut(true));

    const clientType = (await app.getContext()).app.host.clientType;
    if (clientType === HostClientType.web) {
      dispatch(signOutPopup());
      return;
    }

    // desktop
    try {
      await authentication.authenticate({
        url: window.location.origin + '/teams-signout',
        width: 600,
        height: 535,
      });
    } finally {
      dispatch(signOut({msteams: true}));
    }
  }, [dispatch, isTeamsApp]);

  return logout;
};

export const useTeamsMeetConsent = () => {
  const dispatch = useAppDispatch();
  const isTeamsApp = useIsTeamsApp();

  const askForConsent = useCallback(async () => {
    if (!isTeamsApp) return;

    const redirectUrl = await dispatch(getConsentUrlForCalendar()).unwrap();
    if (!redirectUrl) return;

    try {
      const result = await authentication.authenticate({
        url: redirectUrl,
        width: 600,
        height: 535,
      });

      if (result === 'success') {
        await dispatch(withAsyncThunkErrorHandling(() => enableCalendarAfterRedirection()));
      } else {
        dispatch(disconnectCalendar());
      }
    } catch (error) {
      console.debug('askForConsent failed:', error);
      dispatch(disconnectCalendar());
    }
  }, [dispatch, isTeamsApp]);

  return {askForConsent};
};
